import type mixpanelBrowser from 'mixpanel-browser';

type Mixpanel = typeof mixpanelBrowser;

let loaded: Promise<Mixpanel> | null = null;

/**
 * Loads and initialises Mixpanel on first use.
 *
 * Importing it statically put 433kB (129kB gzip) of analytics into the entry
 * chunk, where it blocked first paint on every page load. Nothing about
 * analytics needs to be on the critical path.
 * @return {Promise<Mixpanel>} The initialised Mixpanel instance.
 */
const load = (): Promise<Mixpanel> =>
  (loaded ??= import('mixpanel-browser').then(
    ({ default: mixpanelInstance }) => {
      mixpanelInstance.init(import.meta.env.VITE_MIXPANEL_TOKEN ?? '', {
        debug: import.meta.env.NODE_ENV === 'development',
        autocapture: false,
        record_sessions_percent: 0,
        api_host: 'https://api-eu.mixpanel.com',
      });
      return mixpanelInstance;
    },
  ));

/**
 * Queues a call until the library has loaded. Callbacks registered on the same
 * promise run in registration order, so `identify` before `track` still
 * arrives in that order.
 * @param {(mixpanelInstance: Mixpanel) => void} fn Call to make once loaded.
 */
const call = (fn: (mixpanelInstance: Mixpanel) => void): void => {
  void load()
    .then(fn)
    .catch((error) => {
      console.error('Failed to load Mixpanel', error);
    });
};

export const mixpanel = {
  track: (eventName: string, properties?: Record<string, unknown>) =>
    call((mp) => mp.track(eventName, properties)),
  identify: (id: string) => call((mp) => mp.identify(id)),
  register: (properties: Record<string, unknown>) =>
    call((mp) => mp.register(properties)),
  reset: () => call((mp) => mp.reset()),
  people: {
    set: (properties: Record<string, unknown>) =>
      call((mp) => mp.people.set(properties)),
  },
};
